import { getToken } from '../authentication/authProvider';

/**
 * Every call the admin makes to the NestJS backend goes through here so it
 * carries the staff member's Supabase access token. The backend still allows a
 * good number of these routes through unauthenticated (`@Public()`), but that is
 * being withdrawn — see docs/security/SECURITY_REMEDIATION_PLAN.md phase 2.
 * Sending the header everywhere first makes that withdrawal a no-op.
 */
export const API_URL = (
  process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/'
).trim();

/** True when `url` is an absolute URL on our own backend. */
export function isApiUrl(url: string): boolean {
  return url.trim().startsWith(API_URL);
}

/**
 * Authenticated `fetch` against the backend, returning the raw `Response`.
 *
 * `path` is relative to `API_URL` ('validation/summary/12'), but an absolute URL
 * on the backend is accepted too, for call sites that already built one.
 * Nothing is asserted about the status — the caller keeps whatever `res.ok`
 * handling it already had.
 */
export async function apiFetchResponse(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const token = await getToken();
  const target = path.trim();
  const url = isApiUrl(target) ? target : `${API_URL}${target}`;
  return fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
}

export async function apiFetch(path: string, options: RequestInit = {}) {
  const res = await apiFetchResponse(path, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

/**
 * Authenticated fetch of a binary document. The caller turns the blob into an
 * object URL for a viewer/iframe/download and is responsible for revoking it —
 * a `blob:` URL is how an authenticated document reaches a viewer that cannot
 * itself send an `Authorization` header.
 */
export async function apiFetchBlob(path: string, options: RequestInit = {}): Promise<Blob> {
  const res = await apiFetchResponse(path, options);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.blob();
}

/** Multipart upload (no JSON content-type — the browser sets the boundary). */
export async function apiUpload(path: string, formData: FormData) {
  const res = await apiFetchResponse(path, { method: 'POST', body: formData });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}
